import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/core';

import { ApiService } from 'lib/services/api.service';
import { contains, eachRecursive, isDevServer, queryParams } from 'lib/tools';
import each from 'lodash-es/each';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { RouterService } from 'lib/services/router.service';
import { MetaService } from './meta.service';

const scriptName = 'cms.service';
const stateKeys = {};

@Injectable()
export class CmsService {
    private apiService: ApiService;
    private metaService: MetaService;
    private environment: any;
    private routerService: RouterService;
    private transferState: TransferState;

    cache: any = {};
    isBrowser: boolean;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject('environment') e: any,
        a: ApiService,
        m: MetaService,
        r: RouterService,
        t: TransferState
    ) {
        try {
            this.isBrowser = isPlatformBrowser(this.platformId);
            this.metaService = m;
            this.environment = e;
            this.apiService = a;
            this.routerService = r;
            this.transferState = t;
        } catch (err) {
            console.error(...new ExxComError(530394, scriptName, err).stamp());
        }
    }

    // CORE

    private getUrl = () => {
        try {
            const fullPath = this.routerService.url.fullPath;
            const parts = this.routerService.url.parts || [];

            return fullPath.includes('category') ? fullPath.split('category')[1] : `/${parts.join('/')}`;
        } catch (error) {
            console.error('Error in CmsService.getUrl():', error);
            return '/';
        }
    };

    private async request({
        contentTypeId,
        entryId,
        query,
        limit,
        skip,
        sort,
        references,
        fields,
        includeCount,
        stateKeyName,
    }: {
        contentTypeId: string;
        entryId?: string;
        query?: object;
        limit?: number;
        skip?: number;
        sort?: any;
        references?: string[];
        fields?: string[];
        includeCount?: boolean;
        stateKeyName?: string;
    }) {
        try {
            let stateKey: any;
            let transferredRes: any;

            const request = async () => {
                try {
                    const url = [
                        this.environment.contentstack.baseUrl,
                        `/content_types/${contentTypeId}`,
                        `/entries${entryId ? '/' + entryId : ''}`,
                        `?environment=${this.environment.contentstack[this.environment.envAbbr].name}`,
                        '&locale=en-us',
                    ];
                    if (query) {
                        url.push(`&query=${JSON.stringify(query)}`);
                    }
                    if (limit) {
                        url.push(`&limit=${limit}`);
                    }
                    if (skip) {
                        url.push(`&skip=${skip}`);
                    }
                    if (includeCount) {
                        url.push('&include_count=true');
                    }
                    if (sort && sort.asc) {
                        url.push(`&asc=${sort.asc}`);
                    }
                    if (sort && sort.desc) {
                        url.push(`&desc=${sort.desc}`);
                    }
                    if (references && references.length > 0) {
                        references.forEach((reference: string) => url.push(`&include[]=${reference}`));
                    }
                    if (fields && fields.length > 0) {
                        fields.forEach((field: string) => url.push(`&only[BASE][]=${field}`));
                    }
                    const res = await this.apiService.get(url.join(''), {
                        useFullUrl: true,
                        httpOptions: {
                            headers: {
                                access_token: this.environment.contentstack[this.environment.envAbbr].access_token,
                                api_key: this.environment.contentstack[this.environment.envAbbr].api_key,
                            },
                        },
                    });
                    if (!res.success) {
                        throw res;
                    }
                    this.parseUrls(res.entries);
                    return res;
                } catch (err) {
                    return err;
                }
            };

            const getStateKey = () => {
                // NB: keyed by content-type only. A per-query key was tried (Loop 3) to fix the
                // category-query collision but regressed (server/browser key mismatch → more
                // transfer misses). The spinner fix belongs in the category component instead.
                // Callers that issue a second query against the same content type in one render
                // pass (e.g. related posts alongside the resolved blog post) must pass an
                // explicit stateKeyName to avoid colliding with the first query's payload.
                //
                // ST-3538: the category page issues three `category_page` queries for the same
                // URL — the full top-partial entry (initCmsTopPartial), a fields-only meta query
                // (initCmsData) and a browser-only islandingpage check (SearchSpringService). With
                // a shared key the last server response to land won, and when the fields-only
                // payload won, the browser rendered the CMS top partial with no rows. Each call
                // site now passes its own stable stateKeyName; keep it that way when adding more.
                const keyName = stateKeyName ? stateKeyName : entryId ? entryId : contentTypeId ? contentTypeId : null;
                if (!keyName) {
                    throw new Error('"keyName" is required.');
                }
                let stateKey = stateKeys[keyName];
                if (!stateKey) {
                    stateKey = makeStateKey(keyName);
                }
                return stateKey;
            };

            // Compute the state key and read any SSR-transferred value BEFORE branching.
            // (Previously `transferredRes` was never assigned before this guard, so the
            // read-back branch below was dead code and every call re-fetched Contentstack
            // on both server and browser — the blog double-fetch / spinner. RC-1.)
            stateKey = getStateKey();
            transferredRes = this.transferState.get(stateKey, !this.isBrowser ? 'firstLoad_server' : 'firstLoad_browser');

            if (isDevServer()) {
                // The dev server does not transfer state
                const res = await request();
                if (!res.success) {
                    throw res;
                }
                return res;
            } else if (transferredRes == 'firstLoad_server' || transferredRes == 'firstLoad_browser' || transferredRes == 'subsequentLoads_browser') {
                const res = await request();
                if (!res.success) {
                    throw res;
                }

                if (transferredRes == 'firstLoad_server') {
                    this.transferState.set(stateKey, res);
                } else if (transferredRes == 'firstLoad_browser') {
                    this.transferState.set(stateKey, 'subsequentLoads_browser');
                }
                return res;
            } else {
                // secondLoad_browser — SSR already transferred the data; return it, no refetch.
                this.transferState.set(stateKey, 'subsequentLoads_browser');
                return transferredRes;
            }
        } catch (err) {
            console.error(...new ExxComError(482773, scriptName, err).stamp());
        }
    }

    private parseUrls(entries: any[]) {
        try {
            if (isEmpty(entries)) {
                return;
            }
            const urlKeys = ['url', 'href']; // If other fields are used for URLs in Contentstack, then those need to be added here
            eachRecursive(entries, (v: any, k: string, o: any) => {
                if (!contains(urlKeys, k)) {
                    return;
                }
                const urlParts = v ? decodeURIComponent(v).split('?') : [];
                const urlComponent = urlParts[0];
                const params = urlParts[1];
                o.routerLink = urlComponent
                    ? urlComponent.indexOf('http') == 0 || urlComponent.indexOf('/') == 0 // Full URL or already has a leading slash
                        ? urlComponent
                        : `/${urlComponent}`
                    : '';
                if (o.routerLink.indexOf('.jpg') !== -1 || o.routerLink.indexOf('.jpeg') !== -1 || o.routerLink.indexOf('.png') !== -1) {
                    const dotIndex = o.routerLink.lastIndexOf('.');
                    o.webp = o.routerLink.slice(0, dotIndex) + '.webp';
                }
                o.queryParams = !isEmpty(params) ? queryParams.get(params) : {};
                each(o.queryParams, (v: string, k: string) => (v = decodeURIComponent(v)));
            });
        } catch (err) {
            console.error(...new ExxComError(602993, scriptName, err).stamp());
        }
    }

    async getEntries(
        contentTypeId: string,
        {
            references,
            fields,
            url,
            query,
            sort,
            skip,
            searchParams,
            stateKeyName,
        }: {
            references?: string[];
            fields?: string[];
            url?: string;
            query?: any;
            sort?: any;
            skip?: any;
            searchParams?: any;
            stateKeyName?: string;
        } = {}
    ) {
        try {
            if (url?.includes('#_heatmap')) {
                url = url.split('#_heatmap')[0];
            }

            url = this.processSearchUrl(url, searchParams);
            const req: any = { contentTypeId };
            if (references) {
                req.references = references;
            }
            if (fields) {
                req.fields = fields;
            }
            if (query) {
                req.query = query;
            } else if (url) {
                req.query = { url };
            }
            if (sort) {
                req.sort = sort;
            }
            if (skip) {
                req.skip = skip;
            }
            if (stateKeyName) {
                req.stateKeyName = stateKeyName;
            }
            const res = await this.request(req);
            return res.entries || [];
        } catch (err) {
            console.error(...new ExxComError(298121, scriptName, err).stamp());
        }
    }

    async getEntry(contentTypeId: string, { references, fields }: { references?: string[]; fields?: string[] } = {}) {
        try {
            const res = await this.getEntries(contentTypeId, {
                references,
                fields,
            });
            if (get(res[0], 'seo_meta_data')) {
                this.metaService.add(res[0].seo_meta_data);
            }
            return res[0] || {};
        } catch (err) {
            console.error(...new ExxComError(900101, scriptName, err).stamp());
        }
    }

    // PAGES

    async getPage(
        contentTypeId: string,
        {
            references,
            fields,
            searchParams,
            stateKeyName,
        }: { references?: string[]; fields?: string[]; searchParams?: any; stateKeyName?: string } = {}
    ) {
        try {
            const url = this.getUrl();
            const res = await this.getEntries(contentTypeId, {
                url,
                references,
                fields,
                searchParams,
                stateKeyName,
            });
            if (get(res[0], 'seo_meta_data')) {
                this.metaService.add(res[0].seo_meta_data);
            }
            return res[0] || {};
        } catch (err) {
            console.error(...new ExxComError(629834, scriptName, err).stamp());
        }
    }

    // PARTIALS

    async getPartials(contentTypeId: string, references?: string[]) {
        try {
            const url = this.getUrl();
            return this.getEntries(contentTypeId, { references, url });
        } catch (err) {
            console.error(...new ExxComError(729834, scriptName, err).stamp());
        }
    }

    // BLOG

    async getBlogCategories(contentTypeId: string) {
        try {
            let url = null;
            if (contentTypeId != `${this.environment.siteAbbr}_blog_category`) {
                // Safe access to routerService.url.current with proper null checks
                if (this.routerService && this.routerService.url && this.routerService.url.current) {
                    url = this.routerService.url.current;
                } else {
                    console.warn('RouterService.url.current is undefined in getBlogCategories. Using null.');
                }
            }
            return await this.getEntries(contentTypeId, { url });
        } catch (err) {
            console.error(...new ExxComError(892873, scriptName, err).stamp());
        }
    }

    async getBlogPost(url: string) {
        try {
            const query = { url: url };
            const req = {
                contentTypeId: `${this.environment.siteAbbr}_blog_post`,
                references: [
                    'blog_category',
                    'blog_content_v3.section.group.tables_optional.table_reference',
                    'blog_content_v3.section.group.tables_optional.table_reference.new_table_format_2.table_group.tables.table_reference',
                    'blog_content_v3.section.group.ad_block_reference',
                ],
                query: query,
            };
            const res = await this.request(req);
            return (res.entries && res.entries[0]) || {};
        } catch (err) {
            console.error(...new ExxComError(902873, scriptName, err).stamp());
        }
    }

    async getBlogPosts(limit?: number, skip?: number, includeCount = false, stateKeySuffix?: string) {
        try {
            return await this.searchBlogPosts({}, limit, skip, false, includeCount, stateKeySuffix);
        } catch (err) {
            console.error(...new ExxComError(310992, scriptName, err).stamp());
        }
    }

    async getBlogPostsByCategory(name: string, limit?: number, skip?: number, includeCount = false, stateKeySuffix?: string) {
        try {
            const req = {
                contentTypeId: `${this.environment.siteAbbr}_blog_post`,
                query: { blog_category: { $in_query: { title: { $regex: name, $options: 'i' } } } },
                references: ['blog_category'],
                limit: limit ? limit : 18,
                skip: skip ? skip : 0,
                sort: { desc: 'date' },
                includeCount: includeCount,
                stateKeyName: stateKeySuffix ? `${this.environment.siteAbbr}_blog_post_${stateKeySuffix}` : undefined,
            };
            const res = await this.request(req);

            return includeCount ? res : res.entries;
        } catch (err) {
            console.error(...new ExxComError(482781, scriptName, err).stamp());
        }
    }

    async searchBlogPosts(query: object, limit?: number, skip?: number, isKeyUpEvent?: boolean, includeCount = false, stateKeySuffix?: string) {
        try {
            const req = {
                contentTypeId: `${this.environment.siteAbbr}_blog_post`,
                query: query,
                references: ['blog_category'],
                limit: limit ? limit : 5,
                skip: skip ? skip : 0,
                sort: { desc: 'date' },
                includeCount: includeCount,
                stateKeyName: stateKeySuffix ? `${this.environment.siteAbbr}_blog_post_${stateKeySuffix}` : undefined,
            };
            const res = await this.request(req);

            // RC-3/D1: checkBlogCount removed from the render path. It maintained a
            // write-only Mongo tracker (blog/entry/find + blog/entry/create) that no UI
            // consumer reads, while firing a large fetch + O(n^2) diff on every blog search.

            return includeCount ? res : res.entries;
        } catch (err) {
            console.error(...new ExxComError(539876, scriptName, err).stamp());
        }
    }
    /**
     * @function checkBlogCount
     * @param entries is the entries from content stack for pagination
     * @purpose to track the uid's of each blog and to know the total count of the blogposts for pagination.
     */
    async checkBlogCount(entries: any) {
        const entryList = entries;
        const existingBlogs = await this.findBlog();
        const existingBlogEntries = existingBlogs?.data;
        // Takes entries from content stack and compares them to documents in db

        for (let i = 0; i < entryList.length; i++) {
            if (existingBlogEntries?.length === 0) {
                this.createBlog({
                    uid: entryList[i].uid,
                    category: entryList[i].blog_category[0].title,
                    title: entryList[i].title,
                });
                existingBlogEntries.push({
                    uid: entryList[i].uid,
                    category: entryList[i].blog_category[0].title,
                    title: entryList[i].title,
                });
            }
            let matchingEntry = false;
            for (let j = 0; j < existingBlogEntries.length; j++) {
                if (entryList[i].uid == existingBlogEntries[j].uid) {
                    matchingEntry = true;
                    break;
                }
            }
            if (matchingEntry == false) {
                this.createBlog({
                    uid: entryList[i].uid,
                    category: entryList[i].blog_category[0].title,
                    title: entryList[i].title,
                });
            }
        }
    }
    /**
     * @function createBlog
     * @param field Blog entry data
     * @purpose creates blog entry in database with basic information about the blog (uid, category, title)
     * @returns
     */
    async createBlog(field: Object) {
        try {
            return this.apiService.post('blog/entry/create', field);
        } catch (err) {
            console.error(...new ExxComError(111119, scriptName, err).stamp());
        }
    }
    /**
     * @function findBlog
     * @returns the blogTracker object
     * @purpose creates a blogTracker object if there is not one, otherwise will return the blogTracker Object
     */
    async findBlog() {
        try {
            const blogRes = await this.apiService.get('blog/entry/find');
            return blogRes;
        } catch (err) {
            console.error(...new ExxComError(111119, scriptName, err).stamp());
        }
    }

    async searchBlogPostsByTitle(title: string, limit?: number, skip?: number) {
        try {
            const query = {
                $or: [{ title: { $regex: title, $options: 'i' } }, { tags: { $regex: title, $options: 'i' } }],
            };
            return await this.searchBlogPosts(query, limit, skip, true);
        } catch (err) {
            console.error(...new ExxComError(692883, scriptName, err).stamp());
        }
    }

    async searchBlogPostsByTag(tag: string, limit?: number, skip?: number) {
        try {
            const query = { tags: tag };
            return await this.searchBlogPosts(query, limit, skip, true);
        } catch (err) {
            console.error(...new ExxComError(430200, scriptName, err).stamp());
        }
    }

    /**
     * Search blog posts with optional search term and category filter
     * @param searchTerm - Optional search term to filter by title/tags
     * @param category - Optional category name to filter by (e.g., "AI & ML", "Deep Learning")
     * @param limit - Number of results to return
     * @param skip - Number of results to skip (for pagination)
     * @param includeCount - Whether to include total count in response
     * @returns Promise<any> - Returns entries array or { entries, count } if includeCount is true
     */
    async searchBlogPostsWithFilters(searchTerm?: string, category?: string, limit?: number, skip?: number, includeCount = false) {
        try {
            const query: any = {};
            const conditions: any[] = [];

            // Add search term filter (title or tags)
            if (searchTerm) {
                conditions.push({
                    $or: [{ title: { $regex: searchTerm, $options: 'i' } }, { tags: { $regex: searchTerm, $options: 'i' } }],
                });
            }

            // Add category filter
            if (category && category !== 'Recent') {
                conditions.push({
                    blog_category: {
                        $in_query: {
                            title: { $regex: category, $options: 'i' },
                        },
                    },
                });
            }

            // Combine conditions with $and if we have multiple
            if (conditions.length > 1) {
                query.$and = conditions;
            } else if (conditions.length === 1) {
                Object.assign(query, conditions[0]);
            }

            return await this.searchBlogPosts(query, limit, skip, true, includeCount);
        } catch (err) {
            console.error(...new ExxComError(430201, scriptName, err).stamp());
        }
    }

    // CACHE

    /**
     * @function initCache
     * @param {String} contentTypeId - A Contentstack content type ID
     * @param {Array[]} [references] - A list of references to be populated when retrieving entries
     * @returns {Promise<Object[]>} - A cache entry, which is a list of CMS entries
     * @description Initializes JavaScript-based local caching of CMS data.
     * initCache returns the data that was retreived from Contentstack, so it may
     * not be necessary to call getCached. However, if getCached is used instead
     * of the data returned from initCache, initCache still has to be called
     * before getCached.
     */
    async initCache({
        contentTypeId,
        references,
        searchParams,
        stateKeyName,
    }: {
        contentTypeId: string;
        references?: string[];
        searchParams?: any;
        stateKeyName?: string;
    }): Promise<any> {
        try {
            const url = this.getUrl();
            const entry = await this.getEntries(contentTypeId, {
                references,
                url: this.processSearchUrl(url, searchParams),
                stateKeyName,
            });
            return entry;
        } catch (err) {
            console.error(...new ExxComError(699884, scriptName, err).stamp());
        }
    }

    /**
     * @function getCached
     * @param {String} contentTypeId - A Contentstack content type ID
     * @returns {Object[]} - A cache entry, which is a list of CMS entries
     * @description Retrieves data that was cached previously via initCache.
     * initCache must be called before calling getCached.
     */
    getCached(contentTypeId: string): any {
        try {
            if (!this.isCached(contentTypeId)) {
                throw new Error('Cache must be initialized with initCache before data is retrieved.');
            }
            const cached = this.cache[this.getCacheKey(contentTypeId)];
            return cached;
        } catch (err) {
            console.error(...new ExxComError(699884, scriptName, err).stamp());
        }
    }

    /**
     * @function getCacheKey
     * @param {String} contentTypeId - A Contentstack content type ID
     * @description Generates a cache key.
     */
    private getCacheKey(contentTypeId: string): string {
        try {
            return `${contentTypeId}__${this.getUrl()}`;
        } catch (err) {
            console.error(...new ExxComError(501988, scriptName, err).stamp());
        }
    }

    /**
     * @function isCached
     * @param {String} contentTypeId - A Contentstack content type ID
     * @description Accesses cache to check if we have data stored.
     */
    private isCached(contentTypeId: string): boolean {
        try {
            return !isEmpty(get(this.cache, this.getCacheKey(contentTypeId)));
        } catch (err) {
            console.error(...new ExxComError(920392, scriptName, err).stamp());
        }
    }

    /**
     * @function processSearchUrl
     * @param {String} url - A Contentstack url for an entry
     * @description Adds sarch params to url, else returns the url
     */
    private processSearchUrl(url: string, searchParams?: any) {
        if (url == '/search' && searchParams) {
            const { q } = searchParams;
            if (q) {
                url = `/search?q=${encodeURIComponent(q.split('%20').join('-'))}`;
            }
        }
        return url;
    }
    /**
     * @function checkEntryExistence
     * @param {Object} entry - A contentstack entry - preferably an entry that populates the entire page, not a part of it.
     * @description The purpose of this function is to take in an entry object from content stack. If the entry is empty, we 404.
     * otherwise we return the entry for its intended usage. ONLY use with pages that rely on a single entry. Calling this function
     * on a single entry (such as a single partial in a large collection of them on a page), we will be 404ing the page when there
     * IS FOUND CONTENT on the page.
     * The intention of this function is simply to 404 a page that is dependent on a single entry if it should fail.
     */
    checkEntryExistence(entry: Object) {
        if (isEmpty(entry)) {
            this.routerService.router.navigate(['/404'], { skipLocationChange: true });
        } else {
            return entry;
        }
    }

    async getAllCategoryPages() {
        try {
            const pages = await this.apiService.get('categorypage');
            if (!pages.success) {
                return { data: [], success: false };
            }
            return pages;
        } catch (err) {
            console.error(...new ExxComError(920395, scriptName, err).stamp());
            return { data: [], success: false, error: err };
        }
    }

    async getAllMarketoforms() {
        try {
            // Check if the endpoint exists before calling it
            const pages = await this.apiService.get('marketoform');
            if (!pages.success) {
                console.warn('Marketo forms endpoint not available, returning empty array');
                return { data: [], success: false };
            }
            return pages;
        } catch (err) {
            console.error(...new ExxComError(920396, scriptName, err).stamp());
            return { data: [], success: false, error: err };
        }
    }

    async getHomepage(references: string[] = []) {
        try {
            const res = await this.apiService.get('homepage');
            if (!res.success || !res.data?.url) {
                return this.getPage('homepage', { references });
            }
            if (get(res.data, 'seo_meta_data')) {
                this.metaService.add(res.data.seo_meta_data);
            }
            this.parseUrls([res.data]);
            return res.data;
        } catch (err) {
            console.error(...new ExxComError(920397, scriptName, err).stamp());
            return this.getPage('homepage', { references });
        }
    }

    async getFooterCached() {
        try {
            const res = await this.apiService.get('footer');
            if (!res.success || !res.data?.sections) {
                return this.getEntry('footer');
            }
            return res.data;
        } catch (err) {
            console.error(...new ExxComError(920398, scriptName, err).stamp());
            return this.getEntry('footer');
        }
    }

    async getAllBlogCategories() {
        try {
            const res = await this.apiService.get('blog-category');
            if (!res.success || !res.data?.length) {
                return this.getBlogCategories(`${this.environment.siteAbbr}_blog_category`);
            }
            return res.data;
        } catch (err) {
            console.error(...new ExxComError(920399, scriptName, err).stamp());
            return this.getBlogCategories(`${this.environment.siteAbbr}_blog_category`);
        }
    }
}
